/* eslint-disable prefer-destructuring */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getDaysInMonth, addMonths, lastDayOfMonth, setDate } from 'date-fns';
import { apiGet, apiPost } from '../../../api';

function getNextBillingDate(billingDayOfMonth) {
  const today = new Date();
  let result = today;

  if (billingDayOfMonth !== today.getDate()) {
    const nextMonth = addMonths(today, 1);
    if (getDaysInMonth(nextMonth) <= billingDayOfMonth) {
      result = lastDayOfMonth(nextMonth);
    } else {
      result = setDate(nextMonth, billingDayOfMonth);
    }
  }

  return result;
}

export function useStorageAccountInfo() {
  return useQuery(
    ['storageAccountInfo'],
    async () => {
      try {
        const apiResult = await apiGet(`storage/account?v=${Date.now()}`);
        if (apiResult.ok) {
          const {
            balance: currentBalance,
            charge: monthlyCharge,
            dayofmonth: paymentDayOfMonth,
            cc: fetchedCreditCard,
            noStorageAccount,
          } = await apiResult.json();

          let creditCard = {};

          if (fetchedCreditCard && fetchedCreditCard.length) {
            const address1 = fetchedCreditCard[0].address1;
            const address2 = fetchedCreditCard[0].address2;
            const cardExpDate = fetchedCreditCard[0].cardexpdate;
            const cardExpMmYy = fetchedCreditCard[0].cardexpmmyy;
            const cardType = fetchedCreditCard[0].cardtype;
            const city = fetchedCreditCard[0].city;
            const firstName = fetchedCreditCard[0].firstname;
            const lastName = fetchedCreditCard[0].lastname;
            const state = fetchedCreditCard[0].state;
            const zipCode = fetchedCreditCard[0].zipcode;
            const cardMasked = fetchedCreditCard[0].cardmasked;
            const cardId = fetchedCreditCard[0].cardid;

            creditCard = {
              address1,
              address2,
              cardExpDate,
              cardExpMmYy,
              cardType,
              city,
              firstName,
              lastName,
              state,
              zipCode,
              cardMasked,
              cardId,
            };
          }

          // Pull in some other basic customer info from the legacy `/portal/users/me` API
          const meResult = await apiGet('me', '/portal/users/');
          let email = '';
          let phoneNumber = '';
          if (meResult.ok) {
            const me = await meResult.json();
            email = me.email;
            phoneNumber = me.phone;
          }

          return {
            noStorageAccount,
            currentBalance,
            monthlyCharge,
            nextBillingDate: getNextBillingDate(paymentDayOfMonth),
            creditCard,
            email,
            phoneNumber,
          };
        }

        throw new Error('Unable to fetch account information from MoveEZ.');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        return null;
      }
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      // cacheTime: 1000 * 60 * 60,
      // initialData: null,
    }
  );
}

/**
 * Saves the customer's credit card information.
 */
export function useUpdateStorageAccountCreditCard() {
  const queryClient = useQueryClient();

  return useMutation(
    async (data) => {
      const apiResult = await apiPost(
        `storage/account/credit-card?v=${Date.now()}`,
        data
      );
      if (apiResult.ok) {
        return apiResult.json();
      }

      throw new Error('Unable to update storage account credit card.');
    },
    {
      onSuccess: () => {
        // Invalidate any queries that contain credit card data so that
        // they'll be automatically refetched
        queryClient.invalidateQueries(['storageAccountInfo']);
      },
    }
  );
}

/**
 * Saves the customer's credit card address information.
 */
export function useUpdateStorageAccountBillingAddress() {
  const queryClient = useQueryClient();

  return useMutation(
    async (data) => {
      const apiResult = await apiPost(
        `storage/account/billing-address?v=${Date.now()}`,
        data
      );
      if (apiResult.ok) {
        return apiResult.json();
      }

      throw new Error('Unable to update storage account credit card address.');
    },

    {
      onSuccess: () => {
        queryClient.invalidateQueries(['storageAccountInfo']);
      },
    }
  );
}

export function useStoragePaymentHistory() {
  return useQuery(
    ['storagePaymentHistory'],
    async () => {
      try {
        const apiResult = await apiGet(`storage/payments?v=${Date.now()}`);
        if (apiResult.ok) {
          return await apiResult.json();
        }

        throw new Error('Unable to fetch payment history from MoveEZ.');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        return [];
      }
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60,
    }
  );
}
